/**
 * **Festival Admin's and Money's one card** (`FestivalAdmin.dc.html`, `Money.dc.html`, the
 * design-debt pass, modules 3 and 4). Every
 * screen the drawing draws is the same head (the shared PageHeader) over cards of rows — a name and
 * its line, a sentence, a chip. A row may carry its own action after the chip: the drawing draws
 * none, but a screen's real verbs (End…, Send a reminder) live on the row they act on (canvas
 * defect 215).
 */
import type { ReactNode } from 'react';
import './drawn.css';

export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'mute';
export interface FaRow {
  a: ReactNode;
  b?: ReactNode | undefined;
  c?: ReactNode | undefined;
  chip?: string | null | undefined;
  tone?: Tone | undefined;
  /** The faint wash on the row itself. */
  wash?: 'bad' | 'warn' | 'ok' | undefined;
  /** The sentence in red, where the drawing warns. */
  cBad?: boolean | undefined;
  act?: ReactNode | undefined;
  /** The figure on the row, in Oswald (Money's cards): an amount, a count. */
  amt?: string | null | undefined;
  /** The figure in red, where the drawing marks it. */
  amtBad?: boolean | undefined;
  key?: string | undefined;
}
export interface FaCard {
  head: string;
  sub?: string | null | undefined;
  edge?: 'navy' | 'red' | 'sky' | 'gold' | 'green' | null;
  rows: FaRow[];
  /** What the card says when it holds nothing — never an empty box. */
  empty?: string | undefined;
  /** The card's quieter last line, on the cream. */
  foot?: string | null | undefined;
  key?: string;
  /**
   * Every row's columns line up — the line, the reason, the figure, the chip (James, Oct 13: a
   * reason column that wanders row to row is not a column). A phone still wraps.
   */
  aligned?: boolean;
}

export function FaCards({ cards }: { cards: FaCard[] }): ReactNode {
  return (
    <div className="fa__cards">
      {cards.map((c) => (
        <section
          key={c.key ?? c.head}
          className={`fa__card${c.edge ? ` fa__card--${c.edge}` : ''}${c.aligned ? ' fa__card--aligned' : ''}`}
          aria-label={c.head}
        >
          <div className="fa__head">
            <span className="fa__name">{c.head}</span>
            {c.sub ? <span className="fa__sub">{c.sub}</span> : null}
          </div>
          {c.rows.length === 0 ? (
            <div className="fa__row">
              <span className="fa__a">
                <b>{c.empty ?? 'Nothing here yet.'}</b>
              </span>
            </div>
          ) : (
            c.rows.map((r, i) => (
              <div
                key={r.key ?? String(i)}
                className={`fa__row${r.wash ? ` fa__row--${r.wash}` : ''}`}
              >
                <span className="fa__a">
                  <b>{r.a}</b>
                  {r.b ? <span className="fa__b">{r.b}</span> : null}
                </span>
                <span className={`fa__c${r.cBad ? ' fa__c--bad' : ''}`}>{r.c}</span>
                {r.amt ? (
                  <span className={`fa__amt${r.amtBad ? ' fa__amt--bad' : ''}`}>{r.amt}</span>
                ) : null}
                {r.chip ? (
                  <span className={`fa__chip fa__chip--${r.tone ?? 'mute'}`}>{r.chip}</span>
                ) : null}
                {r.act ? <span className="fa__act">{r.act}</span> : null}
              </div>
            ))
          )}
          {c.foot ? <div className="fa__foot">{c.foot}</div> : null}
        </section>
      ))}
    </div>
  );
}

export interface FaTile {
  n: string;
  label: string;
  sub?: string | undefined;
  tone?: 'green' | 'red' | 'gold' | 'mute' | undefined;
}
/**
 * The strip of figures over the cards (Money's drawing): a figure, its label, its line. `wide` is
 * Money's own strip, where no tile is narrower than 200 — ten tiles wrap to two rows of five.
 */
export function FaStrip({ tiles, wide }: { tiles: FaTile[]; wide?: boolean }): ReactNode {
  if (tiles.length === 0) return null;
  return (
    <div className={wide ? 'fa__strip fa__strip--wide' : 'fa__strip'}>
      {tiles.map((t) => (
        <div className="fa__tile" key={t.label}>
          <div className={`fa__tileN${t.tone ? ` fa__tileN--${t.tone}` : ''}`}>{t.n}</div>
          <div className="fa__tileLabel">{t.label}</div>
          {t.sub ? <div className="fa__tileSub">{t.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

/** One band of the Budget's watch bands: its range, and where the line sits, when it is known. */
export interface FaBand {
  key: string;
  name: string;
  low: number;
  high: number;
  /** Cost as a share of receipts, in percent; null where nothing is read. */
  at: number | null;
}

/**
 * **Watch bands · starting targets** (Rebuild 30, bud06): each band's range on a track from 0 to
 * twice its top, and a mark where the line sits — green inside the range, red outside. Drawn in
 * SVG by its own attributes, so no style is written inline.
 */
export function FaBands({
  bands,
  ratified,
}: {
  bands: FaBand[];
  /** "DRAFT UNTIL A DIRECTOR RATIFIES" — or who ratified them. */
  ratified: string | null;
}): ReactNode {
  const scale = Math.max(...bands.map((b) => b.high * 2), 1);
  const x = (v: number): string => `${String(Math.min(100, (v / scale) * 100))}%`;
  return (
    <section className="fa__card fa__bands" aria-label="WATCH BANDS · STARTING TARGETS">
      <div className="fa__head">
        <span className="fa__name">WATCH BANDS · STARTING TARGETS</span>
        <span className={`fa__chip fa__chip--${ratified ? 'ok' : 'warn'}`}>
          {ratified ?? 'DRAFT UNTIL A DIRECTOR RATIFIES'}
        </span>
        <span className="fa__sub">cost as % of receipts · where this area sits</span>
      </div>
      {bands.map((b) => {
        const inside = b.at !== null && b.at >= b.low && b.at <= b.high;
        return (
          <div className="fa__band" key={b.key}>
            <span>
              <b>{b.name}</b>
              <span className="fa__bandRange">
                {String(b.low)} – {String(b.high)}%
              </span>
            </span>
            <svg
              className="fa__bandTrack"
              viewBox="0 0 100 16"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <rect className="fa__bandBack" x="0" y="0" width="100%" height="16" rx="3" />
              <rect
                className="fa__bandZone"
                x={x(b.low)}
                y="0"
                width={`${String(((b.high - b.low) / scale) * 100)}%`}
                height="16"
                rx="3"
              />
              {b.at !== null ? (
                <rect
                  className={inside ? 'fa__bandMark' : 'fa__bandMark fa__bandMark--out'}
                  x={x(b.at)}
                  y="-3"
                  width="0.6%"
                  height="22"
                />
              ) : null}
            </svg>
            <span
              className={
                b.at === null
                  ? 'fa__bandAt fa__bandAt--none'
                  : inside
                    ? 'fa__bandAt'
                    : 'fa__bandAt fa__bandAt--out'
              }
            >
              {b.at === null ? '—' : `${(Math.round(b.at * 10) / 10).toFixed(1)}%`}
            </span>
          </div>
        );
      })}
    </section>
  );
}

/** One phase of a drawn calendar: its name, what it is, the date it turns on — done, now or ahead. */
export interface FaPhase {
  key: string;
  phase: string;
  what: string;
  sub: ReactNode;
  state: 'done' | 'now' | '';
}

/**
 * **A drawn calendar** (Rebuild 30, bud00): the phases side by side in one row — done in green,
 * now in navy, ahead on paper. A phone stacks them.
 */
export function FaPhases({ phases }: { phases: FaPhase[] }): ReactNode {
  return (
    <ol className="fa__phases" aria-label="The calendar">
      {phases.map((p) => (
        <li
          key={p.key}
          className={`fa__phase${p.state ? ` fa__phase--${p.state}` : ''}`}
          aria-current={p.state === 'now' ? 'step' : undefined}
        >
          <span className="fa__phaseName">{p.phase}</span>
          <b className="fa__phaseWhat">{p.what}</b>
          <span className="fa__phaseSub">{p.sub}</span>
        </li>
      ))}
    </ol>
  );
}

/** One step on a viewer's path (Rebuild 46 bud00): ticked when done, and the one way in. */
export interface FaPathStep {
  key: string;
  n: number;
  title: string;
  sub: ReactNode;
  /** What pressing the step does, in words — "Open the queue →", or the date it waits on. */
  act: string;
  href: string;
  done: boolean;
}

/**
 * **Your path** (Rebuild 46 bud00): the viewer's steps as done / not tiles, like the chair app's
 * Today, under a row naming whose path it is. Every other Budget screen is reached from a step.
 */
export function FaSteps({
  head,
  note,
  steps,
}: {
  head: ReactNode;
  note: ReactNode;
  steps: FaPathStep[];
}): ReactNode {
  return (
    <section className="fa__path" aria-label="Your path">
      <div className="fa__pathHead">
        {head}
        <span className="fa__pathNote">{note}</span>
      </div>
      <ol className="fa__steps">
        {steps.map((st) => (
          <li key={st.key}>
            <a className={st.done ? 'fa__step fa__step--done' : 'fa__step'} href={st.href}>
              <span className="fa__stepTop">
                <span className="fa__stepTick" aria-label={st.done ? 'done' : 'not yet'}>
                  {st.done ? '✓' : ''}
                </span>
                <span className="fa__stepN">STEP {String(st.n)}</span>
              </span>
              <b className="fa__stepTitle">{st.title}</b>
              <span className="fa__stepSub">{st.sub}</span>
              <span className="fa__stepAct">{st.act}</span>
            </a>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** A cell in a drawn table: text with its quieter line, a figure, or a chip. */
export type FaCell =
  | { kind: 'text'; a: ReactNode; b?: ReactNode | null; strong?: boolean }
  | {
      kind: 'num';
      v: string;
      muted?: boolean;
      strong?: boolean;
      bad?: boolean;
      /** Green: the good side of a variance (Rebuild 46 bva). */
      good?: boolean;
      struck?: string;
    }
  | { kind: 'chip'; t: string; tone: Tone }
  /** A control in its cell — the row's one next act, a checkbox. */
  | { kind: 'node'; n: ReactNode };

export interface FaTableRow {
  key: string;
  cells: FaCell[];
  /** The line under the row, across every column — the reason, written from the rule. */
  why?: ReactNode | null;
  whyBad?: boolean;
  wash?: 'bad' | 'warn' | 'ok' | 'sel' | undefined;
}

/**
 * **A drawn table** (Rebuild 30's Budget): a head, a header row, rows whose columns line up on
 * every row, and the reason on a line of its own under each. The column widths are the drawing's,
 * one layout per table shape (`kind`), so every view of the same table aligns.
 */
export function FaTable({
  head,
  sub,
  kind,
  columns,
  rows,
  foot,
  empty,
}: {
  head: string;
  sub?: string | null;
  /**
   * base — line · three years · the baseline · the rule (bud01). two — line · last year · this
   * year. queue — when · line, who and why · from · to · the ceiling (bud08). strip — the cash
   * strip's bag and its five columns (cashstrip). margin — item · price · cost · margin · units ·
   * margin dollars (sq06). bank — date · the bank line · amount · the suggested match · the acts
   * (Rebuild 46 bank). grid — accounts and areas down, the three roles across; chip — a line, its
   * owner chip, who holds it today (Rebuild 46 bud11).
   */
  kind:
    | 'base'
    | 'two'
    | 'queue'
    | 'strip'
    | 'margin'
    | 'polist'
    | 'polines'
    | 'explist'
    | 'erlines'
    | 'bills'
    | 'bva'
    | 'bank'
    | 'grid'
    | 'chip';
  columns: { label: string; right?: boolean }[];
  rows: FaTableRow[];
  foot?: string | null;
  empty?: string;
}): ReactNode {
  return (
    <section className="fa__card fa__tablecard" aria-label={head}>
      <div className="fa__head">
        <span className="fa__name">{head}</span>
        {sub ? <span className="fa__sub">{sub}</span> : null}
      </div>
      <div className={`fa__trow fa__trow--hdr fa__trow--${kind}`} role="row">
        {columns.map((c) => (
          <span
            key={c.label}
            role="columnheader"
            className={`fa__th${c.right ? ' fa__th--right' : ''}`}
          >
            {c.label}
          </span>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="fa__trow fa__trow--empty">
          <span className="fa__tcell">
            <b>{empty ?? 'Nothing here yet.'}</b>
          </span>
        </div>
      ) : (
        rows.map((r) => (
          <div
            key={r.key}
            role="row"
            className={`fa__trow fa__trow--${kind}${r.wash ? ` fa__row--${r.wash}` : ''}`}
          >
            {r.cells.map((c, i) =>
              c.kind === 'chip' ? (
                <span key={i} className="fa__tcell">
                  <span className={`fa__chip fa__chip--${c.tone}`}>{c.t}</span>
                </span>
              ) : c.kind === 'node' ? (
                <span key={i} className="fa__tcell">
                  {c.n}
                </span>
              ) : c.kind === 'num' ? (
                <span
                  key={i}
                  className={`fa__tnum${c.muted ? ' fa__tnum--muted' : ''}${c.strong ? ' fa__tnum--strong' : ''}${c.bad ? ' fa__tnum--bad' : ''}${c.good ? ' fa__tnum--good' : ''}`}
                >
                  {c.struck ? <s className="fa__struck">{c.struck}</s> : null}
                  {c.v}
                </span>
              ) : (
                <span key={i} className="fa__tcell">
                  {c.strong ? <b>{c.a}</b> : c.a}
                  {c.b ? <span className="fa__b">{c.b}</span> : null}
                </span>
              ),
            )}
            {r.why ? (
              <span className={`fa__why${r.whyBad ? ' fa__why--bad' : ''}`}>{r.why}</span>
            ) : null}
          </div>
        ))
      )}
      {foot ? <div className="fa__foot">{foot}</div> : null}
    </section>
  );
}

/** One state on a life row: its name, the day and who — or a dash. */
export interface FaStep {
  state: string;
  word?: string | null;
  at: string | null;
  by: string | null;
}

/**
 * **The life row** (Rebuild 46 po1, er1): every state of one document in a row, with who and when
 * under each — the ones still ahead gray with a dash. A phone stacks them two to a row.
 */
export function FaLife({
  steps,
  foot,
  label = 'THE LIFE ROW',
}: {
  steps: FaStep[];
  foot?: string | null;
  label?: string;
}): ReactNode {
  return (
    <section className="fa__card" aria-label={label}>
      <div className="fa__head">
        <span className="fa__name">{label}</span>
        <span className="fa__sub">every state with who and when under it</span>
      </div>
      <ol className="fa__life">
        {steps.map((x) => (
          <li key={x.state} className={`fa__step${x.at || x.by ? ' fa__step--done' : ''}`}>
            <span className="fa__stepName">{x.state.toUpperCase()}</span>
            <b className="fa__stepAt">{x.at ? `${x.word ? `${x.word} · ` : ''}${x.at}` : '—'}</b>
            {x.by ? <span className="fa__stepBy">{x.by}</span> : null}
          </li>
        ))}
      </ol>
      {foot ? <div className="fa__foot">{foot}</div> : null}
    </section>
  );
}
